// Bare FOLDER imports throughout, resolved per platform by the bundler — the repo-wide rule
// (CLAUDE.md), required in a `shared.ts` (hazard 16), and the sanctioned exception to "shared
// files import only pure modules".
//
// ⚠️ `data/constants` below was `data/constants/shared` until 2026-09-07, under a comment
// claiming "the folder's DESKTOP half reads `localStorage` at module-eval time". CHECKED, and it
// does not: that variant has no imports and no top-level statements at all — the only mention of
// localStorage in it is a comment naming a key that `apiService` uses. Nothing was being avoided
// by the deep import, so it names the folder like everything else.
import {
  CUSTOMER_EDIT_INFO_FOR_LOYALTY,
  EXTERNAL_LOYALTY_PARTNERS_C1,
  EXTERNAL_LOYALTY_PARTNERS_C2,
  EXTERNAL_LOYALTY_PARTNERS_C3,
  EXTERNAL_LOYALTY_PROVIDER_METAIDS,
} from "../../data/constants";
import store from "../../redux-store";
import { refundLoyaltyPoints } from "../../redux-store/slices/Loyalty";
import { saveSaleData } from "../../redux-store/slices/SaleSlice";
// From the SELECTOR BARREL, never from `loyalty/selectors` directly — see that file's header.
// The direct path pulls `helpers/commonHelpers`, whose desktop variant calls
// `createBrowserHistory()` at import time; taking it here broke `loyaltyRefundHelper.test.ts`,
// which mocks this path and cannot mock one it does not know about.
import { selectActiveLoyaltyProvider } from "../../redux-store/selectors/loyaltySelectors";
import { notifyError } from "../../effects-bridge";

/**
 * Uplifted pieces of `helper/loyaltyHelper`.
 *
 * ⚠️ **The FILE exists in both apps (web ~9 KB, mobile 4.2 KB), and NONE of the pure predicates
 * below exists on mobile** — checked symbol by symbol, which is what rule 4 actually asks for.
 * They live here anyway, and that placement is deliberate: every one is a PURE predicate over
 * data the API returns, with no store read and no platform API. `packages/core/loyalty` — the
 * platform-neutral flow written 2026-09-04 — is built on them, so a `.desktop`-only home would
 * have pinned the whole flow to one platform for no reason beyond where the symbol happened to
 * be born. (They sat in `index.desktop.ts` from the uplift until then.)
 *
 * 🔴 **`loyaltyRefundHelper` is now here too, and there is ONE of it — web's.** It was a genuine
 * two-body split until 2026-09-07: web wrapped the caller's callback (saving the refunded sale
 * on success, toasting on failure) and took `skipSave`/`sale`; mobile passed the callback
 * straight through and did neither.
 *
 * That split described nothing worth keeping. The mobile body was **dead code** — a faithful
 * port of `apps/mobile/src/helper/loyaltyHelper.ts:105`, which the dot-pos app defined and never
 * called from anywhere (the old mobile panel had no way to un-redeem at all). DP-8174 gave
 * mobile a Remove button by pointing it at the shared `useLoyaltyRedemption`, and so made a
 * never-executed body live: the refund fired, the sale was never written back, and the panel sat
 * in `applied` for points the backend had already released. The kiosk's points toggle inherited
 * exactly that.
 *
 * So this is the uplift's own rule applied where it was missed — mobile runs web's revamped
 * loyalty flow, and a divergence that only records which platform got the feature first is not a
 * divergence. The failure toast comes along for the same reason the redeem saga's did on
 * 2026-09-05: a silent refund failure IS the mobile weakness being removed.
 *
 * 🔴 **Nothing stays per-platform. Both variant files are now bare re-exports.** `getActiveProvider`
 * was the last holdout, kept in `index.desktop.ts` because only web's app code calls it — but
 * "which platform calls it" was never the test rule 4 asks. The test is whether the BODY can
 * differ, and this one cannot: its `selectActiveLoyaltyProvider` already lives in
 * `redux-store/selectors/loyaltySelectors/shared.ts` and its store read is the same read on
 * either side. Publishing the name on both platforms costs nothing and is what the eight pure
 * predicates above already do.
 */

/**
 * The active loyalty provider, read from the store, or `{}`.
 *
 * ⚠️ Reads app state through core's own store (the 2026-08-21 decision) where the app file used its
 * `store` singleton. Note the `?? {}` — callers rely on getting an object, not `null`, even
 * though the selector itself returns `null` when nothing matches.
 *
 * Web's `saleHelpers` and `apps/web/src/helper/loyaltyHelper` are its only callers today; it is
 * here rather than in `index.desktop.ts` because the body is platform-neutral, not because
 * anyone on the other platform asked for it yet.
 */
export function getActiveProvider(): any {
  return selectActiveLoyaltyProvider(store.getState()) ?? {};
}

/** Is this metaId one of the partners treated as external loyalty (Xeno, Bluetokai)? */
export const isExternalLoyaltyProvider = (metaId?: string) => {
  return !!metaId && EXTERNAL_LOYALTY_PROVIDER_METAIDS.includes(metaId);
};

/** Is this metaId a class-1 partner (Xeno)? Their coupons need a server-side unblock. */
export const isExternalProviderC1 = (metaId?: string) => {
  return !!metaId && EXTERNAL_LOYALTY_PARTNERS_C1.includes(metaId);
};

/** Is this metaId a class-2 partner (Bluetokai)? */
export const isExternalProviderC2 = (metaId?: string) => {
  return !!metaId && EXTERNAL_LOYALTY_PARTNERS_C2.includes(metaId);
};

/** Is this metaId a class-3 partner (Dotpe)? */
export const isExternalProviderC3 = (metaId?: string) => {
  return !!metaId && EXTERNAL_LOYALTY_PARTNERS_C3.includes(metaId);
};

/** Class 1 or 2. */
export const isExternalProviderC1C2 = (metaId?: string) => {
  return isExternalProviderC1(metaId) || isExternalProviderC2(metaId);
};

/**
 * Class 1, 2 or 3.
 *
 * The app's docstrings for this group were transcribed as-is nowhere: they had drifted from the
 * code (the `C3` one read "checks if a loyalty provider is both a Dotpe partner", the `C1C2` one
 * described Xeno AND Bluetokai for an OR, and three orphaned blocks sat above `C2` describing
 * functions that had already moved to core). The BODIES are verbatim; only the prose is new.
 */
export const isExternalProviderC1C2C3 = (metaId?: string) => {
  return isExternalProviderC1(metaId) || isExternalProviderC2(metaId) || isExternalProviderC3(metaId);
};

/**
 * Is this bill discount a redeemed-loyalty-points discount?
 *
 * Identified structurally (`loyaltyPoints` + `dclass === "Discount"`) because these entries carry
 * neither `discountId` nor `mid` — which is exactly why `shouldRetainBillDiscount` has to ask.
 *
 * 🔴 The single source of truth for this test. Never re-derive it inline —
 * `docs/flows/LOYALTY_REFUND.md` explains what that cost in production.
 */
export const isLoyaltyPointDiscountType = (discount: any): boolean => {
  return discount?.loyaltyPoints && discount?.dclass === "Discount";
};

/**
 * Does the bill carry a customer-bound loyalty discount — redeemed points or a partner coupon?
 *
 * If so the customer may not be removed or edited. Both are blocked against that customer on the
 * BACKEND and the unblock APIs need them on the sale, so the change is prevented up front rather
 * than undone afterwards; the cashier releases points through the panel's Remove first.
 *
 * 🔴 The single source of truth for that gate — `docs/flows/LOYALTY_REFUND.md`. Never re-derive
 * it inline, for the same reason `isLoyaltyPointDiscountType` (which it uses) says so.
 *
 * ⚠️ **`apps/mobile` calls this from nowhere**, and that is a real gap rather than a platform
 * choice: `cartBody#handleCustomerReset` clears the customer straight through while points are on
 * the bill. It lived in `apps/web/src/helper/loyaltyHelper.ts` until 2026-09-09, which is why it
 * could not be called from there at all; see `docs/flows/LOYALTY.md`.
 */
export const isCustomerLockedByLoyalty = (saleData: any): boolean =>
  !!saleData?.billDiscountList?.some(
    (discount: any) => discount?.isExternal || isLoyaltyPointDiscountType(discount),
  );

/**
 * Refuse a customer change while the bill is locked, and say why.
 *
 * ```ts
 * if (blockCustomerChangeIfLocked(saleData)) return;   // the whole guard
 * ```
 *
 * 🔴 **Use this rather than testing `isCustomerLockedByLoyalty` and toasting yourself.** That
 * three-line shape was written out at four separate `apps/web` call sites, each importing the
 * predicate and the message separately — the same "the rule is shared but what SURROUNDS it is
 * not" drift the loyalty panel's copy had (`docs/flows/LOYALTY.md`). Here it would have been
 * worse than cosmetic: `apps/mobile` had to grow four of them too, and a site that forgot the
 * toast refuses silently, which reads to the cashier as a dead button.
 *
 * @returns `true` when the action was BLOCKED — the caller should return without doing it.
 *
 * ⚠️ The toast goes through the effects bridge, so this is callable from either platform. It is
 * a no-op when unwired rather than a throw, which means a missing bridge degrades to the silent
 * refusal above rather than to a crash; both apps do wire `notifyError`.
 */
export function blockCustomerChangeIfLocked(saleData: any): boolean {
  if (!isCustomerLockedByLoyalty(saleData)) return false;
  notifyError(CUSTOMER_EDIT_INFO_FOR_LOYALTY);
  return true;
}

/**
 * The loyalty provider whose configured `discountCode` matches this bill discount, or
 * `undefined`.
 *
 * The `!!discount?.discountCode` guard is load-bearing, not defensive noise: without it a
 * discount with no code would match the first provider that also has none.
 */
export function getLoyaltyProviderForDiscount<T extends { meta?: { discountCode?: string } }>(
  loyaltyProviders: T[],
  // Both the discount and its code are optional: callers read the discount off a `find` that can
  // miss, and a points discount that carries no code is exactly what the guard below exists for.
  discount: { discountCode?: string } | undefined | null,
): T | undefined {
  return loyaltyProviders.find(
    (provider) => !!discount?.discountCode && discount.discountCode === provider.meta?.discountCode,
  );
}

/**
 * Refunds redeemed loyalty points against a sale. **ONE body, both platforms** — see this
 * file's header for why the mobile variant was deleted rather than brought into line.
 *
 * @param providerId - `mid` of the loyalty provider holding the points.
 * @param cb - Invoked with the refund response; `res.refundSuccess` indicates success.
 * @param skipSave - When true, the refunded sale is not saved back to the store. Use for a
 *   sale being discarded, so the refund does not overwrite the new/empty sale.
 * @param sale - Sale to refund against. Defaults to the current sale in the store; pass it
 *   explicitly when the store already holds a different sale (e.g. after a reset).
 *
 * ⚠️ Reads and writes app state through core's own store where the app file used its `store`
 * singleton, and raises the failure toast through the effects bridge's `notifyError` where the
 * app file called `ToastNotifyError`. Both are the standing substitutions on this branch.
 *
 * ⚠️ Two LINT-ONLY deviations from the verbatim web body, made when it moved here: `let saleData`
 * became `const`, and `!skipSave && dispatch(...)` became an `if`. Both were eslint errors that
 * travelled with the code, and `prefer-const` is auto-fixable — the pre-commit hook would have
 * rewritten the first silently. Behaviour is identical; nothing else about the body changed.
 *
 * 🔴 The success path dispatches `saveSaleData(res.sale)` BEFORE invoking `cb`, and
 * `loyaltyRefundEffect` depends on that ordering to stay non-re-entrant. Preserve it —
 * `docs/flows/LOYALTY_REFUND.md` §2.
 *
 * 🔴 **Committing the removal is this function's job, not the caller's.** The discount comes off
 * the bill only via the sale the refund RETURNS, and on failure nothing changes — which is the
 * invariant that keeps the customer's blocked points and the local bill in agreement. A caller
 * that strips the discount itself and fires this afterwards reintroduces the production bug
 * `LOYALTY_REFUND.md` opens with.
 */
export const loyaltyRefundHelper: any = (providerId: string, cb: any, skipSave = false, sale?: any) => {
  const saleData = sale || store.getState().saleDataStore.saleData;
  const callback = (res: any) => {
    if (res?.refundSuccess) {
      if (!skipSave) store.dispatch(saveSaleData(res.sale));
    } else {
      notifyError("Failed to remove loyalty points");
    }
    cb?.(res);
  }
  const dataToSend: any = {
    postBody: {
      sale: saleData,
      providerId,
    },
    callback,
  };
  store.dispatch(refundLoyaltyPoints(dataToSend));
};
